import { useT } from '@dia/i18n';
import type { AssetCategory } from '@dia/db';
import { RequireAuth } from './lib/identity';
import { loadAssets, useLoad } from './lib/data';
import { opsContent } from './lib/content';
import { docForAsset, parseSystemsBody } from './lib/systems';
import { Notice, NavRow, Page, Pill } from './lib/ui';

/**
 * The Systems Bible, index. One row per asset from the assets table. The pages
 * themselves live in content/ops/sistemas and are bundled with the app, so the
 * whole section opens with the network off.
 */

const CATEGORY_ORDER: AssetCategory[] = ['power', 'water', 'marine', 'hvac', 'network', 'safety', 'building'];

export function Sistemas() {
  return <RequireAuth>{() => <SistemasBody />}</RequireAuth>;
}

function SistemasBody() {
  const { t, lang } = useT();
  const assets = useLoad(loadAssets, 'assets');
  const docs = opsContent.section('sistemas');
  const list = assets.data ?? [];

  const recorded = (slug: string) => {
    const doc = docForAsset(docs, slug);
    return doc ? parseSystemsBody(doc.body).es.recorded : false;
  };

  // Content pages whose asset is not in the table yet still show, so the section is never empty offline.
  const orphanDocs = docs.filter((d) => !list.some((a) => a.slug === d.meta.asset));

  return (
    <Page title={t('ops.nav.systems')}>
      <p className="mb-4 text-inkMuted">{t('ops.systems.intro')}</p>

      {list.length === 0 && orphanDocs.length === 0 && <Notice tone="warn">{t('ops.systems.no_assets')}</Notice>}

      <div className="flex flex-col gap-6">
        {CATEGORY_ORDER.map((category) => {
          const inCategory = list.filter((a) => a.category === category);
          if (inCategory.length === 0) return null;
          return (
            <section key={category}>
              <h2 className="mb-2 text-[1.15em] font-semibold">{t(`ops.systems.cat.${category}`)}</h2>
              <div className="flex flex-col gap-2">
                {inCategory.map((asset) => (
                  <NavRow
                    key={asset.id}
                    to={`/sistemas/${asset.slug}`}
                    icon="book"
                    title={lang === 'es' ? asset.name_es : asset.name_en}
                    hint={recorded(asset.slug) ? undefined : t('ops.systems.pending_pill')}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {orphanDocs.map((doc) => (
          <NavRow key={doc.path} to={`/sistemas/${String(doc.meta.asset ?? doc.slug)}`} icon="book" title={doc.title[lang]} />
        ))}
      </div>
      {list.length > 0 && (
        <p className="mt-6">
          <Pill tone="neutral">{t('ops.systems.pending_pill')}</Pill>
        </p>
      )}
    </Page>
  );
}
